import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, clearToken } from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import type { ProjectResponse, AiKeyResponse } from '@quicktl/shared';

export default function Dashboard() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState<ProjectResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', sourceLocale: 'en', defaultBranch: 'main', aiProviderKeyId: '' });
  const [creating, setCreating] = useState(false);
  const [aiKeys, setAiKeys] = useState<AiKeyResponse[]>([]);

  async function load() {
    try {
      setProjects((await api.listProjects()).data);
    } catch (e: any) {
      if (e.status === 401) {
        clearToken();
        navigate('/login');
        return;
      }
      setError(e.body?.error || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    api.listAiKeys().then(r => setAiKeys(r.data)).catch(() => {});
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      const { aiProviderKeyId, ...rest } = createForm;
      const project = await api.createProject(aiProviderKeyId ? { ...rest, aiProviderKeyId } : rest);
      setShowCreate(false);
      setCreateForm({ name: '', sourceLocale: 'en', defaultBranch: 'main', aiProviderKeyId: '' });
      navigate(`/projects/${project.id}`);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  }

  return (
    <DashboardLayout>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Projects</h2>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            New project
          </button>
        </div>

        {error && (
          <div role="alert" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {showCreate && (
          <div className="mb-6 p-4 rounded-lg border border-slate-800 bg-slate-900">
            <h3 className="text-sm font-medium mb-3">Create project</h3>
            <form onSubmit={handleCreate} className="space-y-3">
              <label htmlFor="create-project-name" className="sr-only">Project name</label>
              <input
                id="create-project-name"
                type="text"
                placeholder="Project name"
                value={createForm.name}
                onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                required
                className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <div className="flex gap-3">
                <label htmlFor="create-source-locale" className="sr-only">Source locale</label>
                <input
                  id="create-source-locale"
                  type="text"
                  placeholder="Source locale (e.g. en)"
                  value={createForm.sourceLocale}
                  onChange={e => setCreateForm({ ...createForm, sourceLocale: e.target.value })}
                  required
                  className="flex-1 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <label htmlFor="create-default-branch" className="sr-only">Default branch</label>
                <input
                  id="create-default-branch"
                  type="text"
                  placeholder="Default branch (e.g. main)"
                  value={createForm.defaultBranch}
                  onChange={e => setCreateForm({ ...createForm, defaultBranch: e.target.value })}
                  className="flex-1 px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              {aiKeys.length > 0 && (
                <div>
                  <label htmlFor="create-ai-key" className="sr-only">AI key</label>
                  <select
                    id="create-ai-key"
                    value={createForm.aiProviderKeyId}
                    onChange={e => setCreateForm({ ...createForm, aiProviderKeyId: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">No AI key</option>
                    {aiKeys.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
                  </select>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
                >
                  {creating ? 'Creating…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <p className="text-slate-500 text-sm" aria-live="polite">Loading…</p>
        ) : projects.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500">No projects yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {projects.map(p => (
              <div
                key={p.id}
                className="flex items-center justify-between p-4 rounded-lg border border-slate-800 hover:border-slate-700 transition-colors group"
              >
                <button
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="flex-1 text-left"
                >
                  <span className="font-medium text-slate-100 group-hover:text-white">{p.name}</span>
                  <span className="ml-3 text-xs text-slate-500">
                    {p.sourceLocale} → {p.targetLocales.length > 0 ? p.targetLocales.join(', ') : '—'}
                  </span>
                  <span className="ml-3 text-xs text-slate-600">
                    {p.defaultBranch}
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}
    </DashboardLayout>
  );
}
